import type { PotteryStudio } from "@data/studios";

const SITE_ORIGIN = "https://bestpotterystudiosnyc.com";

interface StructuredDataProps {
  studios: PotteryStudio[];
  lastUpdated: string;
}

/**
 * Emits an schema.org ItemList of the ranked studios as JSON-LD.
 *
 * We intentionally describe each studio as a LocalBusiness with position and
 * editorial description only. We do NOT emit AggregateRating/Review markup:
 * those are reserved for genuine, verifiable user ratings, and Google's
 * rich-results policy prohibits self-serving fabricated ratings. An ItemList
 * is the SEO-safe way to communicate an editorial "best of" ranking.
 */
export function StructuredData({ studios, lastUpdated }: StructuredDataProps) {
  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Best Pottery Studios in NYC (2026 Editorial Rankings)",
    description:
      "An editorial comparison of the best pottery and ceramics studios in New York City.",
    url: SITE_ORIGIN,
    dateModified: lastUpdated,
    numberOfItems: studios.length,
    itemListOrder: "https://schema.org/ItemListOrderAscending",
    itemListElement: studios.map((studio) => ({
      "@type": "ListItem",
      position: studio.rank,
      url: studio.website,
      item: {
        "@type": "LocalBusiness",
        name: studio.name,
        url: studio.website,
        description: studio.description,
        address: {
          "@type": "PostalAddress",
          addressLocality: studio.neighborhood,
          addressRegion: "NY",
          addressCountry: "US",
        },
        areaServed: `${studio.borough}, New York City`,
        knowsAbout: studio.classTypes,
        slogan: studio.accolade,
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      // JSON-LD is data, not executable script; serialize it into the tag.
      dangerouslySetInnerHTML={{ __html: JSON.stringify(itemList) }}
    />
  );
}
